import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChatMessage as ChatMessageType } from '../../models/general';
import Icon, { IconName } from '../AppIcon';
import { cn } from '../../utils/cn';
import { findNavPageTitleByPath } from '../layout/navigationModules';
import { formatDistanceToNow } from 'date-fns';
import { ar, fr, enUS } from 'date-fns/locale';

interface ChatMessageProps {
  message: ChatMessageType;
  /** Called after a navigation deep-link is followed, so the widget can close. */
  onNavigate?: () => void;
}

// Localize the relative timestamp to the active UI language (was always English).
const DATE_LOCALES: Record<string, typeof enUS> = { ar, fr };

const ChatMessage: React.FC<ChatMessageProps> = ({ message, onNavigate }) => {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const isUser = message.role === 'user';
  const isError = message.isError || false;
  const dateLocale = DATE_LOCALES[i18n.language?.split('-')[0]] ?? enUS;
  const navItems = !isUser && !isError ? message.navigation ?? [] : [];

  const goTo = (route: string) => {
    navigate(route);
    onNavigate?.();
  };

  return (
    // `flex-row-reverse` follows the inline base direction, so the user's own messages stay on the
    // trailing (end) side in both LTR and RTL.
    <div className={`flex gap-1.5 mb-4 items-start ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      {isError ? (
        <div className="w-8 h-8 rounded-full bg-destructive flex items-center justify-center flex-shrink-0">
          <Icon name="AlertCircle" size={18} className="text-destructive-foreground" />
        </div>
      ) : isUser ? (
        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Icon name="User" size={18} className="text-primary" />
        </div>
      ) : (
        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <img src="/logo192.png" alt={t('AI assistant')} className="w-[18px] h-[18px] object-contain" />
        </div>
      )}

      {/* Message Content. Corners use logical (start/end) radii so the "tail" sits on the correct
          side under RTL as well as LTR. */}
      <div className={`max-w-[75%] flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
        <div
          className={cn(
            'px-3 py-2 rounded-lg text-sm leading-relaxed whitespace-pre-wrap break-words',
            isError && 'bg-destructive/10 text-destructive rounded-ss-sm',
            isUser && !isError && 'bg-primary/10 text-foreground rounded-se-sm',
            !isUser && !isError && 'bg-muted text-foreground rounded-ss-sm'
          )}
        >
          {message.content}
        </div>

        {/* Navigation deep-links: "Go to {page}" affordances the assistant returns
            for "where is X" answers. Client-navigate, then let the widget close. */}
        {navItems.length > 0 && (
          <div className="flex flex-col gap-1.5 mt-2 w-full items-start">
            {navItems.map((item) => {
              const titleKey = findNavPageTitleByPath(item.route);
              const label = titleKey ? t(titleKey) : item.label;
              return (
                <button
                  key={item.route}
                  type="button"
                  onClick={() => goTo(item.route)}
                  className="group flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <Icon name={item.icon as IconName} size={16} className="text-primary flex-shrink-0" />
                  <span className="font-medium">{t('ai.assistant.goTo', { label })}</span>
                  <Icon
                    name="ArrowRight"
                    size={14}
                    className="text-muted-foreground flex-shrink-0 transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5"
                  />
                </button>
              );
            })}
          </div>
        )}

        <span className="text-[0.7rem] text-muted-foreground mt-1 px-1">
          {formatDistanceToNow(new Date(message.timestamp), { addSuffix: true, locale: dateLocale })}
        </span>
      </div>
    </div>
  );
};

export default ChatMessage;
